import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * What {@link useAsyncData} hands back to a component. `loading` is *derived*
 * from the settled state rather than stored, which is what keeps every state
 * write out of the synchronous path of an effect body (#3423).
 */
export interface AsyncDataResult<T> {
    /** Last settled value for the CURRENT request key, else `null`. */
    data: T | null;
    /**
     * Failure of the current key's most recent attempt, else `null`. Typed
     * `unknown` so consumers can narrow it themselves (`isForbiddenError`,
     * `StandardTierRequiredError`, …).
     */
    error: unknown;
    /** True iff a request is outstanding for the current key. Never stored. */
    loading: boolean;
    /**
     * Re-runs the fetcher for the current key. The returned promise resolves
     * once that request has settled and its result is applied, so callers can
     * `await refetch()` before clearing their own spinner.
     *
     * Three contract properties the call sites turn on:
     * 1. A bare `refetch()` is a VISIBLE reload: it changes the request key, so
     *    `data` reverts to `null` and `loading` to `true` for the whole
     *    in-flight window (the surface blanks to its spinner), and a failure
     *    discards the previously loaded data. Pass `{ silent: true }` to
     *    refresh in place instead — that keeps `loading` false, keeps the
     *    current data on screen throughout, and keeps it on failure too.
     * 2. The promise never resolves if the component unmounts while the
     *    request is outstanding. Only `await` it from a handler on a mounted
     *    component.
     * 3. It inherits the `fetcher` stability requirement: an unstable fetcher
     *    re-fires the effect every render, which supersedes each request in
     *    turn and leaves the promise unresolved in practice.
     */
    refetch(options?: { silent?: boolean }): Promise<void>;
}

/** The outcome of one completed attempt, tagged with the key it belongs to. */
interface SettledState<T> {
    /** The request key this outcome belongs to; a stale one is ignored. */
    key: string;
    /** The value the fetcher resolved with, or the last good value on a silent failure. */
    data: T | null;
    /** The rejection this attempt produced, or `null` when it succeeded. */
    error: unknown;
}

/**
 * Runs a keyed async fetch and exposes its result as `{ data, error, loading }`
 * with `loading` derived, never stored.
 *
 * The effect owns the async work directly: it declares its fetch inline and
 * writes state only after the `await`, so nothing is set synchronously from the
 * effect body. In-flight requests are aborted (and their late responses
 * discarded) when the key changes or the component unmounts.
 *
 * @param key Request identity. `null` means idle — no fetch is started,
 *            `loading` is false, and `data` stays `null`.
 * @param fetcher Performs the request. MUST be `useCallback`-stable: it is an
 *                effect dependency, so a fresh function every render re-fetches
 *                on every render.
 * @returns The current data, error, derived loading flag, and a `refetch`.
 */
export function useAsyncData<T>(key: string | null, fetcher: (signal: AbortSignal) => Promise<T>): AsyncDataResult<T> {
    const [settled, setSettled] = useState<SettledState<T> | null>(null);
    const [reloadNonce, setReloadNonce] = useState(0);
    const [silentNonce, setSilentNonce] = useState(0);

    /** Resolvers for `refetch()` promises awaiting the next settle. */
    const waitersRef = useRef<Array<() => void>>([]);

    // Computed during render, never stored. A visible reload bumps the key so
    // `loading` flips true by derivation; a silent reload leaves the key alone
    // so the current data and a false `loading` survive the refresh. The '#'
    // only separates the nonce from the key -- these strings are compared for
    // equality, never parsed.
    const requestKey = key === null ? null : `${reloadNonce}#${key}`;
    const forCurrentKey = settled !== null && settled.key === requestKey;

    const data = forCurrentKey ? settled.data : null;
    const error = forCurrentKey ? settled.error : null;
    const loading = requestKey !== null && !forCurrentKey;

    useEffect(() => {
        if (requestKey === null) {
            // Idle: no request will ever settle here, so release any waiter a
            // superseded run handed back rather than leaving its caller
            // awaiting forever. Matches `refetch`'s own idle path, which
            // resolves immediately.
            const orphaned = waitersRef.current;
            waitersRef.current = [];
            for (const resolve of orphaned) resolve();
            return;
        }

        const controller = new AbortController();

        // Take ownership of the waiters queued for this run. `refetch` pushes
        // its waiter before React commits the nonce bump, so anything pushed
        // after this line belongs to the run that bump will start -- resolving
        // it here would settle a caller's promise on the PREVIOUS response,
        // with the new one still in flight.
        const waiters = waitersRef.current;
        waitersRef.current = [];
        let settledHere = false;

        const run = async (): Promise<void> => {
            try {
                const value = await fetcher(controller.signal);
                if (controller.signal.aborted) return;
                setSettled({ key: requestKey, data: value, error: null });
            } catch (err) {
                if (controller.signal.aborted) return;
                // A failed refresh under an unchanged key keeps the last good
                // value on screen; a new key has nothing to keep.
                setSettled((prev) => ({
                    key: requestKey,
                    data: prev !== null && prev.key === requestKey ? prev.data : null,
                    error: err,
                }));
            }
            if (controller.signal.aborted) return;
            settledHere = true;
            for (const resolve of waiters) resolve();
        };

        void run();

        return () => {
            controller.abort();
            // Superseded before settling: hand the waiters to the run that
            // replaces this one, which is guaranteed to exist because every
            // path that aborts a live request also changes an effect
            // dependency. (Unmount is the exception -- see `refetch`'s JSDoc.)
            if (!settledHere) waitersRef.current = [...waiters, ...waitersRef.current];
        };
        // `silentNonce` is not read in the body: it is here purely as a re-run
        // trigger for a silent refetch, which must not change `requestKey`.
    }, [requestKey, silentNonce, fetcher]);

    const idle = key === null;

    const refetch = useCallback(
        (options?: { silent?: boolean }): Promise<void> => {
            // Nothing will ever settle while idle, so resolve straight away
            // rather than leaving the caller awaiting forever.
            if (idle) return Promise.resolve();

            const settledPromise = new Promise<void>((resolve) => {
                waitersRef.current.push(resolve);
            });

            if (options?.silent === true) {
                setSilentNonce((n) => n + 1);
            } else {
                setReloadNonce((n) => n + 1);
            }

            return settledPromise;
        },
        [idle]
    );

    return { data, error, loading, refetch };
}
